"use client";

import PanelVisualisation from "@tosize/panel-visualisation";
import { cn } from "@tosize/ui";
import { Fragment, useRef } from "react";
import { ErrorBoundary } from "~/app/ErrorBoundary";
import { useMedia } from "~/components/media/Provider";
import { getApiUrl } from "~/lib/server";
import { MediaItem } from "./Item";

interface Props extends Pick<GridOverlayProps, "lang" | "messages" | "cartId"> {
	notAvailable: React.ReactNode;
	children?: React.ReactNode;
	isSalable: boolean;
}

export function MediaGrid({ lang, messages, cartId, notAvailable, children, isSalable }: Props) {
	const { sections: defaultSections, onMediaChange } = useMedia();
	const ref = useRef<HTMLDivElement>(null);

	const sections = defaultSections
		.flatMap((section) => {
			return section.items.map((item, index) => ({ ...item, section: { id: section.id, index } }));
		})
		.splice(0, 6);

	return (
		<Fragment>
			<div
				ref={ref}
				className="relative grid grid-cols-2 gap-4 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5"
			>
				{sections.map((item, i) => {
					const index = isSalable ? i : i + 1;

					return (
						<Fragment key={i}>
							{!isSalable && i === 0 && notAvailable}

							<MediaItem
								item={item}
								onClick={() =>
									onMediaChange({ type: "detail", id: item.section.id, index: item.section.index })
								}
								className={cn({
									"col-span-2 row-span-2 rounded-xl sm:rounded-r-none lg:col-span-3 lg:rounded-xl xl:col-span-4 2xl:col-span-5":
										index === 0,
									"rounded-xl sm:rounded-l-none lg:rounded-r-none lg:rounded-l-xl": index === 1,
									"rounded-xl sm:rounded-l-none lg:rounded-none": index === 2,
									"lg:rounded-r-xl xl:rounded-r-none": index === 3,
									"xl:rounded-r-xl 2xl:rounded-r-none": index === 4,
									"2xl:rounded-r-xl": index === 5,
									hidden: index >= 3,
									"lg:flex": index < 4,
									"xl:flex": index < 5,
									"2xl:flex": index < 6,
									"!rounded-r-xl": index === sections.length - 1,
								})}
							/>
						</Fragment>
					);
				})}

				{children}
			</div>

			<ErrorBoundary>
				<GridOverlay
					lang={lang}
					messages={messages}
					cartId={cartId}
					height={ref.current?.offsetHeight}
				/>
			</ErrorBoundary>
		</Fragment>
	);
}

interface GridOverlayProps {
	lang: string;
	messages: Record<string, string>;
	height?: number;
	cartId?: string;
}

function GridOverlay({ lang, messages, height, cartId }: GridOverlayProps) {
	const { panelData } = useMedia();
	const apiUrl = getApiUrl(lang);

	const hasShape = !!panelData?.workpiece.shape && !!panelData.shape;
	const show = hasShape && panelData.open;

	return (
		<div
			className={cn(
				"-right-8 xl:-right-12 pointer-events-none absolute top-[89.14px] bottom-0 left-0 hidden motion-safe:transition-opacity lg:block",
				{ "invisible opacity-0": !show, "visible opacity-100": !!show },
			)}
		>
			{!!show && (
				<div
					className="pointer-events-auto sticky top-[calc(var(--header-y)+1rem)] z-1 flex items-center justify-center rounded-l-xl bg-shades-light p-4"
					style={{ height }}
				>
					<div className="relative flex h-full w-full items-center justify-center bg-white">
						<PanelVisualisation
							{...panelData}
							apiUrl={apiUrl}
							cartId={cartId}
							lang={lang}
							messages={messages}
						/>
					</div>
				</div>
			)}
		</div>
	);
}
